<template>
  <div class="air__utils__content">
    <!-- Unauthorized Screen if Module Not Enabled for School -->
    <div v-if="!isModuleEnabled" class="mt-4">
      <a-card :bordered="false" class="text-center p-5 shadow-sm">
        <div style="max-width: 550px; margin: 0 auto;">
          <i class="fe fe-lock text-danger mb-3 d-block" style="font-size: 48px;"></i>
          <h4 class="font-weight-bold text-dark mb-2">Module Not Enabled</h4>
          <p class="text-muted mb-4 font-size-14">
            The <strong>Video Lessons</strong> module is not enabled for your school. Please contact your Super Administrator to activate video streaming for this institution.
          </p>
          <router-link to="/dashboard">
            <a-button type="primary" size="large" icon="arrow-left">
              Return to Dashboard
            </a-button>
          </router-link>
        </div>
      </a-card>
    </div>

    <!-- Active Video Lessons Management -->
    <div v-else>
      <div class="air__utils__heading">
        <div class="row align-items-center">
          <div class="col-md-6 col-12 mb-2 mb-md-0">
            <h5 class="mb-0 font-weight-bold">
              <i class="fe fe-video mr-2 text-primary"></i> Video Lessons Management
            </h5>
            <small class="text-muted">Upload and manage educational video lessons streamed to students via Bunny.net</small>
          </div>
          <div class="col-md-6 col-12 text-md-right">
            <a-button v-if="pagepermission.insertable" type="primary" size="large" icon="upload" @click="openUploadModal">
              Upload New Video
            </a-button>
          </div>
        </div>
      </div>

    <!-- Filter Card -->
    <a-card class="mb-4" :bordered="false">
      <div class="row">
        <!-- Session Filter -->
        <div class="col-md-3 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Academic Session</label>
          <a-select v-model="selectedSession" style="width: 100%" @change="handleSessionChange" placeholder="Select Session">
            <a-select-option v-for="s in schoolSessions" :key="s.id" :value="s.id">
              {{ s.name }} <span v-if="s.is_current" class="text-primary font-weight-bold">(Current)</span>
            </a-select-option>
          </a-select>
        </div>

        <!-- Class Filter -->
        <div class="col-md-3 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Class</label>
          <a-select v-model="filterClass" style="width: 100%" @change="handleFilterClassChange" placeholder="All Classes" :loading="classSpinning" allowClear>
            <a-select-option :value="0">All Classes</a-select-option>
            <a-select-option v-for="c in schoolClasses" :key="c.id" :value="c.id">
              {{ c.name }}
            </a-select-option>
          </a-select>
        </div>

        <!-- Section Filter -->
        <div class="col-md-2 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Section</label>
          <a-select v-model="filterSection" style="width: 100%" @change="fetchVideos" placeholder="All Sections" :disabled="!filterClass || filterClass === 0" allowClear>
            <a-select-option :value="0">All Sections</a-select-option>
            <a-select-option v-for="sec in filterSections" :key="sec.id" :value="sec.id">
              {{ sec.name }}
            </a-select-option>
          </a-select>
        </div>

        <!-- Status Filter -->
        <div class="col-md-2 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Status</label>
          <a-select v-model="filterStatus" style="width: 100%" @change="fetchVideos" allowClear placeholder="All Status">
            <a-select-option value="">All</a-select-option>
            <a-select-option value="completed">Ready / Transcoded</a-select-option>
            <a-select-option value="processing">Processing</a-select-option>
            <a-select-option value="queued">Queued</a-select-option>
            <a-select-option value="failed">Failed</a-select-option>
          </a-select>
        </div>

        <!-- Search Box -->
        <div class="col-md-2 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Search</label>
          <a-input-search v-model="searchQuery" placeholder="Title / keywords" @search="fetchVideos" enterButton />
        </div>
      </div>
    </a-card>

    <!-- Videos List Table -->
    <a-card :bordered="false" class="mb-4">
      <div class="table-responsive">
        <a-table
          :columns="columns"
          :dataSource="videos"
          :rowKey="record => record.id"
          :loading="loading"
          :pagination="{ pageSize: 15, showSizeChanger: true }"
        >
          <!-- Thumbnail & Duration Slot -->
          <template slot="thumbnail" slot-scope="text, record">
            <div class="position-relative d-inline-block" style="width: 110px; height: 62px; background: #000; border-radius: 6px; overflow: hidden;">
              <img
                v-if="record.signed_thumbnail_url"
                :src="record.signed_thumbnail_url"
                alt="thumbnail"
                style="width: 100%; height: 100%; object-fit: cover;"
                @error="e => e.target.style.display = 'none'"
              />
              <div v-else class="d-flex align-items-center justify-content-center h-100 text-muted">
                <i class="fe fe-video font-size-18"></i>
              </div>
              <span
                v-if="record.duration_seconds"
                class="badge badge-dark position-absolute"
                style="bottom: 3px; right: 3px; font-size: 10px; padding: 2px 4px; opacity: 0.85;"
              >
                {{ formatDuration(record.duration_seconds) }}
              </span>
            </div>
          </template>

          <!-- Title & Description Slot -->
          <template slot="titleInfo" slot-scope="text, record">
            <div class="font-weight-bold text-dark font-size-14">{{ record.title }}</div>
            <small class="text-muted text-truncate d-block" style="max-width: 280px;" :title="record.description">
              {{ record.description || 'No description provided' }}
            </small>
          </template>

          <!-- Class & Section Slot -->
          <template slot="target" slot-scope="text, record">
            <span class="badge badge-light border text-dark font-weight-bold px-2 py-1">
              {{ record.school_class ? record.school_class.name : 'All Classes' }}
              <span v-if="record.section" class="text-primary font-weight-bold"> - {{ record.section.name }}</span>
              <span v-else class="text-muted"> (All Sections)</span>
            </span>
          </template>

          <!-- Subject Slot -->
          <template slot="subject" slot-scope="text, record">
            <span class="badge badge-primary px-2 py-1 font-size-12">
              {{ record.subject ? record.subject.name : 'General' }}
            </span>
          </template>

          <!-- Transcoding Status Slot -->
          <template slot="status" slot-scope="text, record">
            <span v-if="record.transcoding_status === 'completed'" class="badge badge-success px-2 py-1">
              <i class="fe fe-check-circle mr-1"></i> Ready
            </span>
            <span v-else-if="record.transcoding_status === 'processing'" class="badge badge-info px-2 py-1">
              <a-icon type="loading" class="mr-1" /> Encoding {{ record.encode_progress ? record.encode_progress + '%' : '' }}
            </span>
            <span v-else-if="record.transcoding_status === 'queued'" class="badge badge-warning px-2 py-1">
              <i class="fe fe-clock mr-1"></i> Queued
            </span>
            <span v-else-if="record.transcoding_status === 'failed'" class="badge badge-danger px-2 py-1">
              <i class="fe fe-alert-triangle mr-1"></i> Failed
            </span>
            <span v-else class="badge badge-secondary px-2 py-1">
              {{ record.transcoding_status }}
            </span>
          </template>

          <!-- Actions Slot -->
          <template slot="actions" slot-scope="text, record">
            <div class="text-right text-nowrap">
              <!-- Preview Button -->
              <a-tooltip title="Preview Video">
                <a-button
                  size="small"
                  type="default"
                  class="mr-2"
                  @click="openPreviewModal(record)"
                  :disabled="record.transcoding_status === 'failed' || record.transcoding_status === 'queued'"
                >
                  <i class="fe fe-play text-primary"></i>
                </a-button>
              </a-tooltip>

              <!-- Sync Status Button -->
              <a-tooltip title="Sync status with Bunny">
                <a-button size="small" type="default" class="mr-2" @click="syncVideoStatus(record)" :loading="record._syncing">
                  <i class="fe fe-refresh-cw text-info"></i>
                </a-button>
              </a-tooltip>

              <!-- Edit Button -->
              <a-tooltip title="Edit Details" v-if="pagepermission.editable">
                <a-button size="small" type="default" class="mr-2" @click="openEditModal(record)">
                  <i class="fe fe-edit text-warning"></i>
                </a-button>
              </a-tooltip>

              <!-- Delete Button -->
              <a-popconfirm
                v-if="pagepermission.deletable"
                title="Are you sure you want to delete this video lesson? It will be removed from Bunny CDN."
                okText="Yes, Delete"
                cancelText="Cancel"
                @confirm="deleteVideo(record.id)"
              >
                <a-button size="small" type="danger">
                  <i class="fe fe-trash"></i>
                </a-button>
              </a-popconfirm>
            </div>
          </template>
        </a-table>
      </div>
    </a-card>

    <!-- Upload Video Modal -->
    <a-modal
      v-model="uploadModalVisible"
      title="Upload Educational Video Lesson"
      :footer="null"
      width="680px"
      :maskClosable="!uploading"
      :closable="!uploading"
    >
      <a-form :form="uploadForm" layout="vertical" @submit.prevent="handleStartUpload">
        <div class="row">
          <!-- Title -->
          <div class="col-12">
            <a-form-item label="Video Title" :required="true">
              <a-input
                v-decorator="['title', { rules: [{ required: true, message: 'Please enter a video title' }] }]"
                placeholder="e.g. Chapter 3: Laws of Motion & Friction"
                :disabled="uploading"
              />
            </a-form-item>
          </div>

          <!-- Description -->
          <div class="col-12">
            <a-form-item label="Description / Summary">
              <a-textarea
                v-decorator="['description']"
                placeholder="Key concepts covered, homework references, or chapter notes"
                :rows="3"
                :disabled="uploading"
              />
            </a-form-item>
          </div>

          <!-- Class Selection -->
          <div class="col-md-6 col-12">
            <a-form-item label="Target Class" :required="true">
              <a-select
                v-decorator="['class_id', { rules: [{ required: true, message: 'Please select target class' }] }]"
                placeholder="Select Class"
                @change="handleModalClassChange"
                :disabled="uploading"
              >
                <a-select-option v-for="c in schoolClasses" :key="c.id" :value="c.id">
                  {{ c.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>

          <!-- Section Selection -->
          <div class="col-md-6 col-12">
            <a-form-item label="Section (Optional)">
              <a-select
                v-decorator="['section_id']"
                placeholder="All Sections (Default)"
                allowClear
                :disabled="uploading || modalSections.length === 0"
              >
                <a-select-option :value="undefined">All Sections</a-select-option>
                <a-select-option v-for="sec in modalSections" :key="sec.id" :value="sec.id">
                  {{ sec.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>

          <!-- Subject Selection -->
          <div class="col-md-6 col-12">
            <a-form-item label="Subject" :required="true">
              <a-select
                v-decorator="['subject_id', { rules: [{ required: true, message: 'Please select a subject' }] }]"
                placeholder="Select Subject"
                :disabled="uploading || modalSubjects.length === 0"
              >
                <a-select-option v-for="sub in modalSubjects" :key="sub.id" :value="sub.id">
                  {{ sub.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>

          <!-- Video File Picker / Dropzone -->
          <div class="col-12 mt-2">
            <label class="font-weight-bold mb-2 text-dark">
              Select Video File <span class="text-danger">*</span> (MP4, MOV, MKV, AVI, WebM)
            </label>
            <div
              class="border rounded p-4 text-center cursor-pointer position-relative"
              :class="{ 'bg-light': !selectedFile, 'bg-white border-primary': selectedFile }"
              style="border: 2px dashed #d9d9d9 !important; transition: all 0.3s;"
              @click="triggerFileInput"
            >
              <input
                type="file"
                ref="fileInputRef"
                accept="video/mp4,video/quicktime,video/x-matroska,video/x-msvideo,video/webm"
                style="display: none;"
                @change="handleFileSelected"
                :disabled="uploading"
              />

              <div v-if="!selectedFile">
                <i class="fe fe-upload-cloud font-size-36 text-primary mb-2 d-block"></i>
                <h6 class="font-weight-bold mb-1">Click to select video file from your computer</h6>
                <p class="text-muted font-size-12 mb-0">Files are uploaded directly to Bunny.net CDN with zero server timeouts</p>
              </div>

              <div v-else class="text-left">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <i class="fe fe-film font-size-24 text-success mr-3"></i>
                    <div>
                      <div class="font-weight-bold text-dark">{{ selectedFile.name }}</div>
                      <small class="text-muted">{{ (selectedFile.size / (1024 * 1024)).toFixed(2) }} MB</small>
                    </div>
                  </div>
                  <a-button v-if="!uploading" type="link" class="text-danger" @click.stop="selectedFile = null">
                    Change File
                  </a-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Upload Progress Section -->
          <div v-if="uploading" class="col-12 mt-4">
            <div class="d-flex justify-content-between font-size-13 mb-1">
              <span class="font-weight-bold text-primary">{{ uploadStatusText }}</span>
              <span class="font-weight-bold">{{ uploadProgress }}%</span>
            </div>
            <a-progress :percent="uploadProgress" :status="uploadProgress === 100 ? 'success' : 'active'" :strokeWidth="10" />
            <small class="text-muted d-block mt-1">Please do not close this window while the file is transferring...</small>
          </div>
        </div>

        <!-- Modal Footer Actions -->
        <div class="mt-4 pt-3 border-top text-right">
          <a-button class="mr-2" @click="uploadModalVisible = false" :disabled="uploading">
            Cancel
          </a-button>
          <a-button type="primary" html-type="submit" :loading="uploading" :disabled="!selectedFile">
            {{ uploading ? 'Uploading...' : 'Start Upload' }}
          </a-button>
        </div>
      </a-form>
    </a-modal>

    <!-- Edit Video Modal -->
    <a-modal
      v-model="editModalVisible"
      title="Edit Video Lesson Details"
      okText="Save Changes"
      :confirmLoading="editSaving"
      @ok="handleSaveEdit"
      width="600px"
    >
      <a-form :form="editForm" layout="vertical">
        <a-form-item label="Video Title" :required="true">
          <a-input v-decorator="['title', { rules: [{ required: true, message: 'Please enter title' }] }]" />
        </a-form-item>
        <a-form-item label="Description">
          <a-textarea v-decorator="['description']" :rows="3" />
        </a-form-item>
        <a-form-item label="Publish Status">
          <a-switch v-decorator="['is_active', { valuePropName: 'checked' }]" />
          <span class="ml-2">Make available to students</span>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- Video Preview Modal -->
    <a-modal
      v-model="previewModalVisible"
      :title="previewVideo ? previewVideo.title : 'Video Preview'"
      :footer="null"
      width="820px"
      :destroyOnClose="true"
    >
      <div v-if="previewVideo" class="bg-dark rounded overflow-hidden" style="position: relative; padding-top: 56.25%; min-height: 380px;">
        <iframe
          v-if="previewVideo.preview_embed_url"
          :src="previewVideo.preview_embed_url"
          loading="lazy"
          style="border: none; position: absolute; top: 0; left: 0; height: 100%; width: 100%;"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowfullscreen="true"
        ></iframe>
        <div v-else class="p-5 text-center text-white position-absolute w-100" style="top: 25%;">
          <i class="fe fe-alert-circle font-size-36 text-warning mb-3 d-block"></i>
          <h5>Stream URL not ready yet</h5>
          <p class="text-muted">The video is currently being processed by Bunny.net.</p>
        </div>
      </div>
      <div v-if="previewVideo" class="mt-3">
        <div class="row">
          <div class="col-md-8">
            <span class="badge badge-primary mr-2">{{ previewVideo.subject ? previewVideo.subject.name : 'Subject' }}</span>
            <span class="badge badge-light border">{{ previewVideo.school_class ? previewVideo.school_class.name : '' }} {{ previewVideo.section ? '- ' + previewVideo.section.name : '(All Sections)' }}</span>
            <p class="text-muted mt-2 mb-0">{{ previewVideo.description || 'No description provided.' }}</p>
          </div>
          <div class="col-md-4 text-right">
            <small class="text-muted d-block">Duration: {{ formatDuration(previewVideo.duration_seconds) }}</small>
            <small class="text-muted d-block">Views: {{ previewVideo.views_count || 0 }}</small>
          </div>
        </div>
      </div>
    </a-modal>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { find } from 'lodash'

export default {
  name: 'BunnyVideosManagement',
  data() {
    return {
      loading: false,
      videos: [],
      schoolSessions: [],
      schoolClasses: [],
      filterSections: [],
      modalSections: [],
      modalSubjects: [],
      selectedSession: undefined,
      filterClass: 0,
      filterSection: 0,
      filterStatus: '',
      searchQuery: '',
      classSpinning: false,
      pagepermission: {
        insertable: true,
        editable: true,
        viewable: true,
        deletable: true,
      },

      // Upload Modal
      uploadModalVisible: false,
      uploadForm: this.$form.createForm(this),
      selectedFile: null,
      uploading: false,
      uploadProgress: 0,
      uploadStatusText: '',

      // Edit Modal
      editModalVisible: false,
      editSaving: false,
      editingVideoId: null,
      editForm: this.$form.createForm(this),

      // Preview Modal
      previewModalVisible: false,
      previewVideo: null,

      columns: [
        { title: 'Video', dataIndex: 'thumbnail', scopedSlots: { customRender: 'thumbnail' }, width: 130 },
        { title: 'Title & Summary', dataIndex: 'title', scopedSlots: { customRender: 'titleInfo' } },
        { title: 'Target Class', dataIndex: 'target', scopedSlots: { customRender: 'target' } },
        { title: 'Subject', dataIndex: 'subject', scopedSlots: { customRender: 'subject' } },
        { title: 'Status', dataIndex: 'status', scopedSlots: { customRender: 'status' }, width: 140 },
        { title: 'Actions', scopedSlots: { customRender: 'actions' }, width: 180, className: 'text-right' },
      ],
    }
  },
  computed: {
    isModuleEnabled() {
      const user = this.$store.state.user.user
      if (!user) return false
      if (user.role === 'superadmin') return true
      const modules = (user.school && user.school.modules) || []
      return modules.includes('video_lessons')
    },
  },
  mounted() {
    if (this.isModuleEnabled) {
      this.checkPermissions()
      this.fetchSessions()
    }
  },
  methods: {
    checkPermissions() {
      if (this.$roleutility) {
        this.$roleutility.checkpermission('video-lessons', 22).then((data) => {
          if (data && data.pagePermission) {
            this.pagepermission = data.pagePermission
          }
        }).catch(() => {})
      }
    },
    // ─── Sessions, Classes, Sections, Subjects ──────────────────────
    fetchSessions() {
      this.$http.get('/api/session').then(({ data }) => {
        this.schoolSessions = data.data || []
        const current = find(this.schoolSessions, ['is_current', 1])
        if (current) {
          this.selectedSession = current.id
        } else if (this.schoolSessions.length > 0) {
          this.selectedSession = this.schoolSessions[0].id
        }
        this.fetchClasses()
        this.fetchVideos()
      })
    },

    handleSessionChange(val) {
      this.selectedSession = val
      this.fetchClasses()
      this.fetchVideos()
    },

    fetchClasses() {
      this.classSpinning = true
      this.$http.get('/api/class', { params: { session_id: this.selectedSession } }).then(({ data }) => {
        this.schoolClasses = data.data || []
        this.classSpinning = false
      }).catch(() => {
        this.classSpinning = false
      })
    },

    handleFilterClassChange(val) {
      this.filterClass = val
      this.filterSection = 0
      if (val && val !== 0) {
        this.$http.get(`/api/section/class/${val}`).then(({ data }) => {
          this.filterSections = data.data || []
        })
      } else {
        this.filterSections = []
      }
      this.fetchVideos()
    },

    handleModalClassChange(classId) {
      this.uploadForm.setFieldsValue({ section_id: undefined, subject_id: undefined })
      this.modalSections = []
      this.modalSubjects = []
      if (!classId) return

      // Load sections
      this.$http.get(`/api/section/class/${classId}`).then(({ data }) => {
        this.modalSections = data.data || []
      })

      // Load subjects
      this.$http.get(`/api/subject/class/${classId}`).then(({ data }) => {
        this.modalSubjects = data.data.normal_subjects || data.data || []
      })
    },

    // ─── Fetch Videos ───────────────────────────────────────────────
    fetchVideos() {
      this.loading = true
      const params = {
        session_id: this.selectedSession,
        class_id: this.filterClass && this.filterClass !== 0 ? this.filterClass : undefined,
        section_id: this.filterSection && this.filterSection !== 0 ? this.filterSection : undefined,
        status: this.filterStatus || undefined,
        search: this.searchQuery || undefined,
      }

      this.$http.get('/api/bunny-videos', { params }).then(({ data }) => {
        this.videos = (data.data || []).map(v => ({ ...v, _syncing: false }))
        this.loading = false
      }).catch((err) => {
        this.loading = false
        this.$notification.error({ message: 'Error', description: err.message || 'Failed to load videos' })
      })
    },

    // ─── Direct Upload to Bunny (Axios Binary Stream) ───────────────
    openUploadModal() {
      this.selectedFile = null
      this.uploading = false
      this.uploadProgress = 0
      this.uploadStatusText = ''
      this.modalSections = []
      this.modalSubjects = []
      this.uploadModalVisible = true
      this.$nextTick(() => {
        this.uploadForm.resetFields()
      })
    },

    triggerFileInput() {
      if (!this.uploading && this.$refs.fileInputRef) {
        this.$refs.fileInputRef.click()
      }
    },

    handleFileSelected(e) {
      const file = e.target.files[0]
      if (file) {
        this.selectedFile = file
      }
    },

    handleStartUpload() {
      this.uploadForm.validateFields((err, values) => {
        if (err) return
        if (!this.selectedFile) {
          this.$message.warning('Please select a video file to upload.')
          return
        }

        this.uploading = true
        this.uploadProgress = 0
        this.uploadStatusText = 'Initializing upload on Bunny.net...'

        // 1. Create upload ticket on Laravel backend
        this.$http.post('/api/bunny-videos/upload-ticket', {
          title: values.title,
          description: values.description,
          class_id: values.class_id,
          section_id: values.section_id,
          subject_id: values.subject_id,
          session_id: this.selectedSession,
        }).then(({ data }) => {
          const uploadCreds = data.data.upload_credentials
          this.uploadStatusText = 'Streaming video file directly to Bunny CDN...'

          // 2. Direct binary PUT upload to Bunny Stream endpoint
          axios.put(uploadCreds.direct_upload_url, this.selectedFile, {
            headers: {
              'AccessKey': uploadCreds.library_api_key,
              'Content-Type': 'application/octet-stream',
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total > 0) {
                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                this.uploadProgress = percent
                this.uploadStatusText = `Uploading to Bunny.net (${percent}% of ${(progressEvent.total / (1024 * 1024)).toFixed(1)} MB)...`
              }
            },
          }).then(() => {
            this.uploadProgress = 100
            this.uploadStatusText = 'Upload completed! Transcoding started.'
            this.$notification.success({
              message: 'Video Uploaded Successfully',
              description: 'Your video has been uploaded to Bunny.net and is now being encoded to multi-resolution HLS.',
            })
            setTimeout(() => {
              this.uploadModalVisible = false
              this.uploading = false
              this.fetchVideos()
            }, 1200)
          }).catch((uploadError) => {
            this.uploading = false
            console.error('Direct Bunny upload error:', uploadError)
            this.$notification.error({
              message: 'Upload Failed',
              description: 'Failed to stream video to Bunny.net. Please check network connection and try again.',
            })
          })
        }).catch((apiError) => {
          this.uploading = false
          const errMsg = (apiError.response && apiError.response.data && apiError.response.data.message) || 'Could not generate upload ticket.'
          this.$notification.error({
            message: 'Initialization Failed',
            description: errMsg,
          })
        })
      })
    },

    // ─── Sync Status ────────────────────────────────────────────────
    syncVideoStatus(record) {
      record._syncing = true
      this.$http.post(`/api/bunny-videos/${record.id}/sync`).then(({ data }) => {
        record._syncing = false
        this.$message.success('Status updated from Bunny.net')
        this.fetchVideos()
      }).catch(() => {
        record._syncing = false
        this.$message.error('Failed to sync status')
      })
    },

    // ─── Edit Video ─────────────────────────────────────────────────
    openEditModal(record) {
      this.editingVideoId = record.id
      this.editModalVisible = true
      this.$nextTick(() => {
        this.editForm.setFieldsValue({
          title: record.title,
          description: record.description,
          is_active: record.is_active,
        })
      })
    },

    handleSaveEdit() {
      this.editForm.validateFields((err, values) => {
        if (err) return
        this.editSaving = true
        this.$http.put(`/api/bunny-videos/${this.editingVideoId}`, values).then(() => {
          this.editSaving = false
          this.editModalVisible = false
          this.$notification.success({ message: 'Success', description: 'Video details updated.' })
          this.fetchVideos()
        }).catch((e) => {
          this.editSaving = false
          this.$notification.error({ message: 'Error', description: e.message || 'Failed to update' })
        })
      })
    },

    // ─── Delete Video ───────────────────────────────────────────────
    deleteVideo(id) {
      this.$http.delete(`/api/bunny-videos/${id}`).then(() => {
        this.$notification.success({ message: 'Deleted', description: 'Video lesson deleted successfully.' })
        this.fetchVideos()
      }).catch((e) => {
        this.$notification.error({ message: 'Error', description: e.message || 'Failed to delete' })
      })
    },

    // ─── Preview Modal ──────────────────────────────────────────────
    openPreviewModal(record) {
      this.previewVideo = record
      this.previewModalVisible = true
    },

    formatDuration(secs) {
      if (!secs) return '0:00'
      const m = Math.floor(secs / 60)
      const s = Math.floor(secs % 60)
      return `${m}:${s < 10 ? '0' : ''}${s}`
    },
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.font-size-36 {
  font-size: 36px;
}
.font-size-24 {
  font-size: 24px;
}
.font-size-18 {
  font-size: 18px;
}
.font-size-14 {
  font-size: 14px;
}
.font-size-12 {
  font-size: 12px;
}
</style>
